/**
 * Vanilla-DOM consent modal shown on the visitor's page when an agent asks
 * to interact (move cursor / click). No framework dependency — the recorder
 * runs inside arbitrary host pages. Focus-trapped; ESC and click-outside =
 * deny. Host pages with their own consent UI register an `onControlRequest`
 * handler at the SDK boundary; this modal is the default.
 */

export interface ConsentModalInfo {
  agentName: string;
  brandName: string;
}

export type ConsentDecision = 'granted' | 'denied';

const ROOT_ID = 'cobrowsing-consent-modal';

/** Matches the dashboard's `CONTROL_REQUEST_TIMEOUT_MS` so both sides agree on lapse. */
const AUTO_DENY_TIMEOUT_MS = 30_000;

/** Single-slot. Concurrent `show()` calls return the same pending promise. */
let active: { promise: Promise<ConsentDecision>; close(d: ConsentDecision): void } | null = null;

export function showConsentModal(info: ConsentModalInfo): Promise<ConsentDecision> {
  if (active) return active.promise;

  let resolveDecision!: (d: ConsentDecision) => void;
  const promise = new Promise<ConsentDecision>((resolve) => {
    resolveDecision = resolve;
  });

  const root = buildModal(info, (decision) => {
    close(decision);
  });
  document.body.appendChild(root);

  const previousFocus = document.activeElement as HTMLElement | null;
  const allowBtn = root.querySelector<HTMLButtonElement>('[data-cobrowse-allow]');
  allowBtn?.focus();

  const autoDenyTimer = window.setTimeout(() => close('denied'), AUTO_DENY_TIMEOUT_MS);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close('denied');
    } else if (e.key === 'Tab') {
      const focusables = root.querySelectorAll<HTMLElement>('button');
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener('keydown', onKey, true);

  const close = (d: ConsentDecision): void => {
    if (active === null) return;
    clearTimeout(autoDenyTimer);
    document.removeEventListener('keydown', onKey, true);
    teardown();
    previousFocus?.focus?.();
    resolveDecision(d);
  };

  function teardown(): void {
    root.remove();
    active = null;
  }

  active = { promise, close };
  return promise;
}

function buildModal(info: ConsentModalInfo, onDecision: (d: ConsentDecision) => void): HTMLElement {
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', `${ROOT_ID}-title`);
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '2147483646',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
  } satisfies Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.10), 0 10px 10px -5px rgba(0,0,0,0.04)',
    padding: '24px',
    maxWidth: '380px',
    width: 'calc(100% - 32px)',
    color: '#111827',
  } satisfies Partial<CSSStyleDeclaration>);

  const agentLabel = info.agentName?.trim() || 'A support agent';
  const brandLabel = info.brandName?.trim();

  const title = document.createElement('h2');
  title.id = `${ROOT_ID}-title`;
  title.textContent = `${agentLabel} wants to interact with your screen`;
  Object.assign(title.style, {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.4',
  } satisfies Partial<CSSStyleDeclaration>);

  const body = document.createElement('p');
  body.textContent = brandLabel
    ? `If you allow, ${agentLabel} from ${brandLabel} will be able to move your cursor and click on your page. You can end the session at any time.`
    : `If you allow, the agent will be able to move your cursor and click on your page. You can end the session at any time.`;
  Object.assign(body.style, {
    margin: '0 0 20px 0',
    fontSize: '13px',
    lineHeight: '1.5',
    color: '#4b5563',
  } satisfies Partial<CSSStyleDeclaration>);

  const actions = document.createElement('div');
  Object.assign(actions.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  } satisfies Partial<CSSStyleDeclaration>);

  const deny = document.createElement('button');
  deny.type = 'button';
  deny.dataset['cobrowseDeny'] = '';
  deny.textContent = 'Deny';
  Object.assign(deny.style, {
    padding: '8px 14px',
    border: '1px solid #d1d5db',
    background: '#ffffff',
    color: '#111827',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  deny.addEventListener('click', () => onDecision('denied'));

  const allow = document.createElement('button');
  allow.type = 'button';
  allow.dataset['cobrowseAllow'] = '';
  allow.textContent = 'Allow';
  Object.assign(allow.style, {
    padding: '8px 14px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#ffffff',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  } satisfies Partial<CSSStyleDeclaration>);
  allow.addEventListener('click', () => onDecision('granted'));

  actions.appendChild(deny);
  actions.appendChild(allow);
  card.appendChild(title);
  card.appendChild(body);
  card.appendChild(actions);
  root.appendChild(card);

  // Click-outside on the backdrop = deny.
  root.addEventListener('click', (e) => {
    if (e.target === root) onDecision('denied');
  });

  return root;
}
